<template>
  <div>
    <component :is="formComponent" v-if="formComponent" :key="`${careType}-${section}`" />
    <generic-error v-else button-value="Back to profile" :button-to="profileUrl" />
  </div>
</template>

<script lang="ts">
import { careTypeRouteToGraphqlKey } from './lib/careTypeUtils';
// General components
import GenericError from '@/components/generic_error/GenericError.vue';
import BioForm from './forms/general/BioForm.vue';
import EducationForm from './forms/general/EducationForm.vue';
import SpecialtySkillsForm from './forms/general/SpecialtySkillsForm.vue';
import CertificationsForm from './forms/general/CertificationsForm.vue';

// Housekeeping specific
import HousekeepingRate from './forms/housekeeping/RateForm.vue';
import HousekeepingServices from './forms/housekeeping/ServicesForm.vue';
import HousekeepingExperience from './forms/housekeeping/ExperienceForm.vue';

// Pet care specific
import PetCareExperience from './forms/pet_care/ExperienceForm.vue';
import PetCareRate from './forms/pet_care/RateForm.vue';
import PetCareServices from './forms/pet_care/ServicesForm.vue';

// Babysitter specific
import ChildcareRateForm from './forms/child_care/RateForm.vue';

// Companion care specific
import CompanionCareRate from './forms/companionCare/RateForm.vue';
import CompanionCareServices from './forms/companionCare/ServicesForm.vue';
import CompanionCareExperience from './forms/companionCare/ExperienceForm.vue';
import CompanionCareSpecialtySkills from './forms/companionCare/SpecialtySkillsForm.vue';

// Child care (nanny + babysitter)
import ChildCareSpecialtySkills from './forms/child_care/SpecialtySkillsForm.vue';
import ChildCareSpecialNeeds from './forms/child_care/SpecialNeedsForm.vue';
import ChildCareExperienceForm from './forms/child_care/ExperienceForm.vue';
import ChildCareServicesForm from './forms/child_care/Services.vue';
import HomeworkHelpForm from './forms/child_care/HomeworkHelpForm.vue';

type SectionRegistry = Record<string, any>;

const FORM_REGISTRY: Record<string, SectionRegistry> = {
  housekeeping: {
    bio: BioForm,
    rates: HousekeepingRate,
    services: HousekeepingServices,
    experience: HousekeepingExperience,
    education: EducationForm,
    'specialty-skills': SpecialtySkillsForm,
  },
  petCare: {
    bio: BioForm,
    education: EducationForm,
    experience: PetCareExperience,
    'specialty-skills': SpecialtySkillsForm,
    rates: PetCareRate,
    services: PetCareServices,
  },
  babysitter: {
    bio: BioForm,
    education: EducationForm,
    experience: ChildCareExperienceForm,
    rates: ChildcareRateForm,
    services: ChildCareServicesForm,
    'homework-help': HomeworkHelpForm,
    certifications: CertificationsForm,
    'special-needs': ChildCareSpecialNeeds,
    'specialty-skills': ChildCareSpecialtySkills,
  },
  nanny: {
    bio: BioForm,
    education: EducationForm,
    experience: ChildCareExperienceForm,
    rates: ChildcareRateForm,
    services: ChildCareServicesForm,
    'homework-help': HomeworkHelpForm,
    certifications: CertificationsForm,
    'special-needs': ChildCareSpecialNeeds,
    'specialty-skills': ChildCareSpecialtySkills,
  },
  companionCare: {
    bio: BioForm,
    rates: CompanionCareRate,
    services: CompanionCareServices,
    'additional-services': CompanionCareServices,
    experience: CompanionCareExperience,
    education: EducationForm,
    'specialty-skills': CompanionCareSpecialtySkills,
    certifications: CertificationsForm,
  },
};

export default {
  name: 'ProfileEditFormDispatcher',
  components: {
    GenericError,
    CompanionCareSpecialtySkills,
    ChildCareSpecialtySkills,
    ChildCareSpecialNeeds,
  },
  props: {
    section: {
      type: String,
      required: true,
    },
  },
  computed: {
    careType(): string {
      const param = this.$route.params.care_type;
      return Array.isArray(param) ? param[0] : param;
    },
    resolvedSection(): string {
      if (this.section) return this.section;
      const leaf = this.$route.path.split('/').filter(Boolean).pop();
      return leaf || '';
    },
    formComponent(): any {
      const group = careTypeRouteToGraphqlKey[this.careType as keyof typeof careTypeRouteToGraphqlKey];
      return FORM_REGISTRY[group]?.[this.section] || null;
    },
    profileUrl(): string {
      const sitterUuid = this.$store.getters['account/getAccount']?.id;
      return `/provider/profile/${this.careType}/${sitterUuid}`;
    },
  },
};
</script>
